import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UnsubscribeController } from '@personal-area/global/common'
import { I18nService } from '@personal-area/global/i18n';
import { LoadingState } from '@personal-area/global/state';
import { LoggedUser } from '@personal-area/global/users/models';
import { ButtonComponent } from '@personal-area/global/ui'

import { UserFacade } from '@personal-area/global/users/services';
import { WINDOW } from '@personal-area/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import snsWebSdk from '@sumsub/websdk';
import { AnyEventName } from '@sumsub/websdk/types/types';
import { filter, first, firstValueFrom, map, switchMap, takeUntil, withLatestFrom } from 'rxjs';
import {
  SkipData,
  SkipVerificationDialogComponent,
} from '../../../components/skip-verification-dialog/skip-verification-dialog.component';
import { IDVerificationStatus } from '../../../models/verification-profile-response';
import { IdentityVerificationFacade } from '../../../state/facades/identity';
import { SummaryVerificationFacade } from '../../../state/facades/summary';

const HEIGHT_DELTA = 10;

@Component({
  templateUrl: './identity-auto.component.html',
  styleUrls: ['./identity-auto.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ButtonComponent,
    CommonModule,
    TranslateModule,
  ],
})
export class IdentityAutoComponent
  extends UnsubscribeController
  implements OnInit {
  public isRejected$ = this._summaryFacade.summary$.pipe(
    map(({ idStatus }) => idStatus === IDVerificationStatus.rejected),
  );
  public rejectReason$ = this._identityVerificationFacade.rejectReason$;

  private _lastFrameHeight = 0;

  constructor(
    private _identityVerificationFacade: IdentityVerificationFacade,
    private _summaryFacade: SummaryVerificationFacade,
    private _i18nService: I18nService,
    private _userFacade: UserFacade,
    private _router: Router,
    private _route: ActivatedRoute,
    @Inject(WINDOW) private _window: Window,
    private _dialog: MatDialog,
    private _translateService: TranslateService,
  ) {
    super();
  }

  public ngOnInit() {
    this._identityVerificationFacade.flow$
      .pipe(
        first(
          (flow) =>
            flow.loadingState === LoadingState.loaded && !!flow.accessToken,
        ),
        withLatestFrom(
          this._userFacade.isUserLoaded$.pipe(
            filter(Boolean),
            switchMap(() => this._userFacade.user$),
          ),
        ),
        takeUntil(this._unsubscribe),
      )
      .subscribe(([flowSettings, user]) =>
        this._initSDK(flowSettings.accessToken, flowSettings.countryCode, user),
      );

    this._translateService.onLangChange
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(() => {
        this._identityVerificationFacade.getRequest();
      });
  }

  public promptSkip(): void {
    this._dialog.open(SkipVerificationDialogComponent, <MatDialogConfig<SkipData>>{
      panelClass: 'regular-popup-container',
      direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
      data: {
        text: 'verification-modal.text.skip.full',
      },
    });
  }

  private _getSuccessToken(): Promise<string> {
    this._identityVerificationFacade.getFlow();

    const token$ = this._identityVerificationFacade.flow$.pipe(
      first(
        (flow) =>
          flow.loadingState === LoadingState.loaded && !!flow.accessToken,
      ),
      map((flow) => flow.accessToken),
    );
    return firstValueFrom(token$);
  }

  private _initSDK(
    accessToken: string,
    countryCode: string,
    user: LoggedUser,
  ): void {
    snsWebSdk
      .init(accessToken, () => this._getSuccessToken())
      .withConf({
        lang: this._i18nService.languageA2,
        theme: 'light',
        email: user.email,
        country: countryCode,
      })
      .withOptions({
        addViewportTag: true,
        adaptIframeHeight: true,
      })
      .on('idCheck.onResize', ({ height: newHeight }) => {
        if (Math.abs(this._lastFrameHeight - newHeight) > HEIGHT_DELTA
          && this._lastFrameHeight
        ) {
          this._window.scroll({ top: 0, behavior: 'smooth' });
        }
        this._lastFrameHeight = newHeight;
      })
      .on('idCheck.onAgreementSigned' as AnyEventName, async (payload: any) => {
        if (payload?.residency === 'USA') {
          await this._router.navigate(['../us-rejected'], { relativeTo: this._route });
        }
      })
      .on('idCheck.onApplicantStatusChanged', async (payload) => {
        if (payload.reviewStatus === 'pending') {
          this._summaryFacade.mergeStatus({
            idStatus: IDVerificationStatus.pending,
          });
          await this._router.navigateByUrl('/dashboard');
        }
      })
      .build()
      .launch('.sumsub-placeholder');
  }
}
